import {
  WalletDashboardHeader,
  WalletSummarySection,
  WalletAgentAnalyticsSection,
  WalletActivitySection,
  WalletDistributionSection,
  WalletHighValueSection,
  getWalletTransactionTypes,
  getFormattedAgentWalletCards,
} from "../../../components/reportDashboard/wallet/wallet";
import {
  useAgentWalletAnalytics,
  useWalletActivityTrends,
  useWalletHighValueActivity,
  useWalletSummary,
  useWalletTopHolders,
  useWalletTransactionDistribution,
} from "../../../hooks/useReportDashboard";
import { getErrorMessage } from "../../../utils/error";

export default function Wallet() {
  const {
    data: walletSummaryData,
    isLoading: walletSummaryLoading,
    isError: walletSummaryError,
    error: walletSummaryErrObj,
    refetch: walletSummaryRefetch,
  } = useWalletSummary();

  const {
    data: walletActivityTrendsData,
    isLoading: walletActivityTrendsLoading,
    isError: walletActivityTrendsError,
    error: walletActivityTrendsErrObj,
    refetch: walletActivityTrendsRefetch,
  } = useWalletActivityTrends();

  const {
    data: walletTransactionDistributionData,
    isLoading: walletTransactionDistributionLoading,
    isError: walletTransactionDistributionError,
    error: walletTransactionDistributionErrObj,
    refetch: walletTransactionDistributionRefetch,
  } = useWalletTransactionDistribution();

  const {
    data: walletTopHoldersData,
    isLoading: walletTopHoldersLoading,
    isError: walletTopHoldersError,
    error: walletTopHoldersErrObj,
    refetch: walletTopHoldersRefetch,
  } = useWalletTopHolders();

  const {
    data: walletHighValueActivityData,
    isLoading: walletHighValueActivityLoading,
    isError: walletHighValueActivityError,
    error: walletHighValueActivityErrObj,
    refetch: walletHighValueActivityRefetch,
  } = useWalletHighValueActivity();

  const {
    data: agentWalletAnalyticsData,
    isLoading: agentWalletAnalyticsLoading,
    isError: agentWalletAnalyticsError,
    error: agentWalletAnalyticsErrObj,
    refetch: agentWalletAnalyticsRefetch,
  } = useAgentWalletAnalytics();

  const walletSummaryState = {
    loading: walletSummaryLoading,
    error: walletSummaryError,
    message: getErrorMessage(walletSummaryErrObj) || "Failed to load wallet summary",
    onRetry: walletSummaryRefetch,
  };

  const walletActivityState = {
    loading: walletActivityTrendsLoading,
    error: walletActivityTrendsError,
    message:
      getErrorMessage(walletActivityTrendsErrObj) ||
      "Failed to load activity trends",
    onRetry: walletActivityTrendsRefetch,
  };

  const walletDistributionState = {
    loading: walletTransactionDistributionLoading,
    error: walletTransactionDistributionError,
    message:
      getErrorMessage(walletTransactionDistributionErrObj) ||
      "Failed to load transaction distribution",
    onRetry: walletTransactionDistributionRefetch,
  };

  const walletTopHoldersState = {
    loading: walletTopHoldersLoading,
    error: walletTopHoldersError,
    message:
      getErrorMessage(walletTopHoldersErrObj) || "Failed to load top holders",
    onRetry: walletTopHoldersRefetch,
  };

  const walletHighValueState = {
    loading: walletHighValueActivityLoading,
    error: walletHighValueActivityError,
    message:
      getErrorMessage(walletHighValueActivityErrObj) ||
      "Failed to load high-value activity",
    onRetry: walletHighValueActivityRefetch,
  };

  const agentWalletAnalyticsState = {
    loading: agentWalletAnalyticsLoading,
    error: agentWalletAnalyticsError,
    message:
      getErrorMessage(agentWalletAnalyticsErrObj) ||
      "Failed to load agent analytics",
    onRetry: agentWalletAnalyticsRefetch,
  };

  const summaryMetrics = Object.values(walletSummaryData?.data || {});
  const transactionTypes = getWalletTransactionTypes(
    walletTransactionDistributionData,
  );
  const formattedAgentWalletCards =
    getFormattedAgentWalletCards(agentWalletAnalyticsData);
  const walletActivityData = walletActivityTrendsData?.data || [];
  const topWalletHolders = walletTopHoldersData?.data || [];
  const highValueWalletActivity = walletHighValueActivityData?.data || [];

  return (
    <div className="space-y-6">
      <WalletDashboardHeader />

      <WalletSummarySection
        state={walletSummaryState}
        summaryMetrics={summaryMetrics}
      />

      <WalletAgentAnalyticsSection
        analyticsState={agentWalletAnalyticsState}
        topHoldersState={walletTopHoldersState}
        agentWalletCards={formattedAgentWalletCards}
        topHolders={topWalletHolders}
      />

      <WalletActivitySection
        state={walletActivityState}
        activityData={walletActivityData}
      />

      <WalletDistributionSection
        distributionState={walletDistributionState}
        topHoldersState={walletTopHoldersState}
        transactionTypes={transactionTypes}
        topHolders={topWalletHolders}
      />

      <WalletHighValueSection
        state={walletHighValueState}
        activityData={highValueWalletActivity}
      />
    </div>
  );
}
